(ns manage.ad-variant.details-ocp.core
  (:require ["@mui/material/Alert$default" :as Alert]
            ["@mui/material/AlertTitle$default" :as AlertTitle]
            ["@mui/material/CircularProgress$default" :as CircularProgress]
            ["@mui/material/FormControl$default" :as FormControl]
            ["@mui/material/List$default" :as List]
            ["@mui/material/ListItem$default" :as ListItem]
            ["@mui/material/MenuItem$default" :as MenuItem]
            ["@mui/material/Select$default" :as Select]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Tab$default" :as Tab]
            ["@mui/material/Tabs$default" :as Tabs]
            ["src/svg/inform/error-diamond-fill.svg$default" :as ErrorIcon]
            [common.flags :refer [use-flag]]
            [common.ocp.view :refer [defocp ocp]]
            [common.queries :refer [use-query]]
            [manage.ad-variant.data :as variant-data]
            [manage.ad-variant.details-ocp.details :refer [section variant-details]]
            [manage.ad-variant.details-ocp.preview :refer [variant-preview]]
            [manage.ad-variant.queries :as q]
            [manage.ad-variant.widgets :as w]
            [manage.adset-decision-tree-page.view.table.meta :as meta-table]
            [manage.adset-decision-tree-page.view.table.tiktok :as tiktok-table]
            [manage.adset.hooks :refer [use-channel]]
            [mate.core :as mc]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$ defhook defui]]
            [uix.re-frame :refer [use-subscribe]]))

(rf/reg-event-fx ::ocp-close
  (fn [_]
    {:router/update-query #(dissoc % :variant)}))

(defhook use-ocp-state-from-query-params []
  (let [variant-uuid (:variant (use-subscribe [:router/query-parameters]))
        is-enabled (use-flag :activation-details-side-panel)]
    (uix/use-effect
     (fn []
       (when is-enabled
         (if variant-uuid
           (rf/dispatch [:ocp/open {:id ::ocp
                                    :props {:variant-uuid variant-uuid}
                                    :on-close [::ocp-close]}])
           (rf/dispatch [:ocp/close]))))
     [variant-uuid is-enabled])))

(defui variant-errors [{:keys [variant]}]
  (let [{:keys [components]} variant
        errors (keep (fn [[k component]]
                       (when (:error component)
                         [k (get-in component [:error :message 0])])) components)]
    (when (seq errors)
      ($ Alert
         {:severity "error"
          :icon ($ ErrorIcon)}
         ($ AlertTitle "Something went wrong")
         ($ List
            {:sx #js {:listStyleType "disc"
                      :py 0
                      :pl 2}}
            (for [[k message] errors]
              ($ ListItem
                 {:key k
                  :sx #js {:display "list-item"
                           :p 1}}
                 ($ :strong (name k))
                 ($ :br)
                 message)))))))

(defui integration-errors [{:keys [strategy-row]}]
  (let [channel (use-channel)
        error-message (case channel
                        "tiktok" (tiktok-table/get-integration-error-message strategy-row)
                        "meta" (get-in strategy-row [:integration-error :message])
                        nil)
        error-type (get-in strategy-row [:integration-error :type])]
    (when error-message
      ($ Alert
         {:severity "warning"
          :icon ($ ErrorIcon)}
         ($ AlertTitle "Integration error" (when (= error-type "cm360-error") " (CM360)"))
         ($ Stack {:gap 1}
            error-message)))))

(defui tabs [{:keys [current-tab-id set-current-tab-id]}]
  ($ Tabs {:value current-tab-id}
     (for [{:keys [id label]} [{:id :preview :label "Preview"}
                               {:id :details :label "Details"}]]
       ($ Tab
          {:key id
           :value id
           :onClick (fn [^js _event]
                      (set-current-tab-id id))
           :data-testid (str "tab-" id)
           :label label}))))

(defhook use-ocp-state [{:keys [variant-uuid strategy-row]
                         {formats :data-items} :strategy-row}]
  (let [[format-uuid set-format-uuid] (uix/use-state (:ad-variant-id (first formats)))
        {:keys [advertiser-id adset-id]} (use-subscribe [:url-context])

        {{:keys [ad-variant-uuid] :as variant} :data variant-loading :isLoading}
        (use-query (q/ad-variant-by-uuid-query {:advertiser-id advertiser-id
                                                :adset-id adset-id
                                                :ad-uuid (or format-uuid variant-uuid)}))

        {{revisions :data} :data revisions-loading :isLoading}
        (use-query (q/ad-variant-revisions-query
                    {:advertiser-id advertiser-id
                     :adset-id adset-id
                     :ad-variant-uuid ad-variant-uuid
                     :enabled (nil? strategy-row)}))

        [revision set-revision] (uix/use-state nil)
        revision->variant (uix/use-memo #(mc/index-by (comp :revision :data) :data revisions)
                                        [revisions])]
    (uix/use-effect (fn [] (when (and revisions variant)
                             (set-revision (some #(when (= (:uuid %) (str variant-uuid))
                                                    (:revision %))
                                                 (vals revision->variant)))))
                    [revision->variant revisions variant variant-uuid])
    {:variant (or (get revision->variant revision) variant)
     :format-uuid format-uuid
     :set-format-uuid set-format-uuid
     :is-loading (or revisions-loading variant-loading)
     :revision->variant revision->variant
     :revision revision
     :set-revision set-revision}))

(defui revision-selector [{:keys [revision set-revision revision->variant]}]
  ($ FormControl {:size "small"}
     ($ Select {:value (or revision "")
                :onChange (fn [^js e]
                            (set-revision (.-value (.-target e))))
                :displayEmpty true}
        (for [rev (sort (keys revision->variant))]
          ($ MenuItem {:key rev :value rev}
             (str "Revision id " rev))))))

(defui format-selector [{:keys [formats]
                         {:keys [format-uuid set-format-uuid]} :ocp-state}]
  ($ FormControl {:size "small" :sx #js {:minWidth "20rem"}}
     ($ Select {:value (or format-uuid "")
                :onChange (fn [^js e]
                            (set-format-uuid (.-value (.-target e))))
                :displayEmpty true}
        (for [{:keys [ad-variant-id] :as format} formats]
          ($ MenuItem {:key ad-variant-id :value ad-variant-id}
             ($ w/ad-variant-type-info
                {:ad-variant (update format :ad-type variant-data/legacy->new-ad-type)}))))))

(defui ad-variant-ocp
  [{:keys [strategy-row assigned-node-data] :as props}]
  (let [{:keys [variant is-loading] :as ocp-state} (use-ocp-state props)
        [current-tab-id set-current-tab-id] (uix/use-state :preview)]
    ($ ocp {:title (:content-id variant)}
       ($ Stack {:gap 2 :sx #js {:height "100%" :minHeight 0}}
          (when-not strategy-row
            ($ section {:label "Version"}
               ($ revision-selector ocp-state)))
          ($ variant-errors {:variant variant})
          (when strategy-row
            ($ integration-errors {:strategy-row strategy-row}))
          ($ tabs {:current-tab-id current-tab-id
                   :set-current-tab-id set-current-tab-id})
          (if is-loading
            ($ Stack {:sx #js {:width "100%"
                               :height "100%"
                               :justifyContent "center"
                               :alignItems "center"}}
               ($ CircularProgress))
            (case current-tab-id
              :preview ($ :<>
                          (when-let [formats (:data-items strategy-row)]
                            ($ section {:label "Formats"}
                               ($ format-selector {:formats formats
                                                   :ocp-state ocp-state})))
                          ($ variant-preview {:key (:uuid variant)
                                              :variant variant
                                              :fill-available-height true})
                          (if strategy-row
                            ($ section {:label "Revision id"}
                               (:revision variant))
                            ($ w/render-status {:status (:ad-variant-status variant)
                                                :variant variant
                                                :view-mode :select})))
              :details ($ variant-details {:variant variant
                                           :strategy-row strategy-row
                                           :assigned-node-data assigned-node-data})
              nil))))))

(defocp ocp-view ::ocp {:width "min(720px, 100%)"} [props]
  ($ ad-variant-ocp props))
